import React, { useState, useEffect } from "react";
import { FaTimes, FaPlus, FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./AddEntryModal.css";
import { API_BASE } from "../api/config";

// Error-Tag zu Tabellen-Mapping
export const ERROR_TABLE_MAP = {
  missing_sender: { table: "absender", label: "Absender" },
  prefix_xxx: { table: "schema_keywords", label: "Schema-Keyword" },
  missing_empfaenger: { table: "empfaenger", label: "Empfänger" },
  missing_invoice: { table: "special_rules", label: "Sonderregel" },
  missing_date: { table: "special_rules", label: "Sonderregel" },
  missing_vertragsnr: { table: "special_rules", label: "Sonderregel" },
};

// Verfügbare Tabellen für manuelle Auswahl
const TABLES = [
  { value: "absender", label: "Absender" },
  { value: "empfaenger_person", label: "Empfänger (Person)" },
  { value: "empfaenger_objekt", label: "Empfänger (Objekt)" },
  { value: "empfaenger_vertrag", label: "Empfänger (Vertrag)" },
  { value: "schema_keywords", label: "Schema-Keyword" },
  { value: "special_rules", label: "Sonderregel" },
];

/**
 * AddEntryModal - Modal zum Hinzufügen von DB-Einträgen
 * @param {boolean} isOpen - Modal offen
 * @param {function} onClose - Schließen-Callback
 * @param {function} onSaved - Callback nach erfolgreichem Speichern (table, data)
 * @param {string} defaultTable - Vorausgewählte Tabelle basierend auf Error-Tag
 * @param {object} prefillData - Vorausgefüllte Daten aus OCR (sender_candidates, etc.)
 * @param {string} errorTag - Der Error-Tag der Datei (optional)
 */
const AddEntryModal = ({ isOpen, onClose, onSaved, defaultTable, prefillData = {}, errorTag }) => {
  const [selectedTable, setSelectedTable] = useState(defaultTable || "absender");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form-Felder
  const [formData, setFormData] = useState({
    // Absender / Empfänger
    bezeichnung: "",
    kuerzel: "",
    schema: "",
    aktiv: true,
    priority: 100,
    // Schema-Keywords
    schema_code: "",
    keyword: "",
    match_mode: "exact",
    // Special Rules
    scope: "global",
    scope_value: "",
    target_field: "sender",
    target_op: "set",
    anchor_token: "",
    extract_pattern: "",
  });

  // Reset beim Öffnen / Tabellenwechsel
  useEffect(() => {
    if (isOpen) {
      setSelectedTable(defaultTable || "absender");
      setError(null);
      
      // Prefill aus OCR-Daten
      const prefill = {
        bezeichnung: "",
        kuerzel: "",
        schema: "",
        aktiv: true,
        priority: 100,
        schema_code: "",
        keyword: "",
        match_mode: "exact",
        scope: "global",
        scope_value: "",
        target_field: "sender",
        target_op: "set",
        anchor_token: "",
        extract_pattern: "",
      };

      // Sender-Candidates → Bezeichnung
      if (prefillData.sender_candidates?.length > 0) {
        prefill.bezeichnung = prefillData.sender_candidates[0];
        // Kürzel aus erstem Wort generieren (ohne Längenbeschränkung)
        const firstWord = prefill.bezeichnung.split(/\s+/)[0] || "";
        prefill.kuerzel = firstWord;
      }

      // Prefix-Candidates → Schema-Keyword
      if (prefillData.prefix_candidates?.length > 0) {
        prefill.keyword = prefillData.prefix_candidates[0];
        prefill.schema_code = prefillData.doc_schema || "rechnung";
      }

      // Schema aus Datei
      if (prefillData.doc_schema) {
        prefill.schema = prefillData.doc_schema;
        prefill.schema_code = prefillData.doc_schema;
      }

      setFormData(prefill);
    }
  }, [isOpen, defaultTable, prefillData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);

    try {
      let endpoint = "";
      let payload = {};

      // Endpoint und Payload basierend auf Tabelle (wie in StrgPage)
      if (selectedTable === "absender") {
        endpoint = "/database/absender";
        payload = {
          bezeichnung: formData.bezeichnung.trim(),
          kuerzel: formData.kuerzel.trim(),
          priority: parseInt(formData.priority) || 100,
        };
        if (!payload.bezeichnung || !payload.kuerzel) {
          throw new Error("Bezeichnung und Kürzel sind Pflichtfelder");
        }
      } 
      else if (selectedTable.startsWith("empfaenger")) {
        const category = selectedTable.replace("empfaenger_", "");
        endpoint = `/database/empfaenger?category=${category}`;
        payload = {
          bezeichnung: formData.bezeichnung.trim(),
          kuerzel: formData.kuerzel.trim(),
          priority: parseInt(formData.priority) || 100,
          ...(category === "objekt" ? { person: "" } : { schema: formData.schema || "" }),
        };
        if (!payload.bezeichnung) {
          throw new Error("Bezeichnung ist ein Pflichtfeld");
        }
      }
      else if (selectedTable === "schema_keywords") {
        endpoint = "/database/schema_keywords";
        payload = {
          schema_code: formData.schema_code.trim(),
          keyword: formData.keyword.trim(),
          match_mode: formData.match_mode,
          priority: parseInt(formData.priority) || 100,
          enabled: formData.aktiv ? 1 : 0,
        };
        if (!payload.schema_code || !payload.keyword) {
          throw new Error("Schema und Keyword sind Pflichtfelder");
        }
      }
      else if (selectedTable === "special_rules") {
        endpoint = "/database/special_rules";
        payload = {
          scope: formData.scope,
          scope_value: formData.scope_value.trim(),
          target_field: formData.target_field,
          target_op: formData.target_op,
          anchor_token: formData.anchor_token.trim(),
          extract_pattern: formData.extract_pattern.trim(),
          aktiv: formData.aktiv ? 1 : 0,
          priority: parseInt(formData.priority) || 100,
        };
        if (!payload.anchor_token) {
          throw new Error("Such-Token ist ein Pflichtfeld");
        }
      }

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }

      const result = await res.json();
      onSaved?.(selectedTable, { ...payload, id: result.id || result.inserted });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Render Formular basierend auf Tabelle
  const renderForm = () => {
    switch (selectedTable) {
      case "absender":
        return renderAbsenderForm();
      case "empfaenger_person":
      case "empfaenger_objekt":
      case "empfaenger_vertrag":
        return renderEmpfaengerForm();
      case "schema_keywords":
        return renderSchemaKeywordsForm();
      case "special_rules":
        return renderSpecialRulesForm();
      default:
        return renderAbsenderForm();
    }
  };

  const renderAbsenderForm = () => (
    <>
      <div className="form-group">
        <label>Bezeichnung *</label>
        <input
          type="text"
          value={formData.bezeichnung}
          onChange={(e) => handleChange("bezeichnung", e.target.value)}
          placeholder="z.B. Deutsche Telekom"
        />
        {prefillData.sender_candidates?.length > 1 && (
          <div className="suggestions">
            <span className="suggestions-label">Vorschläge:</span>
            {prefillData.sender_candidates.slice(0, 3).map((s, i) => (
              <button 
                key={i} 
                type="button"
                className="suggestion-chip"
                onClick={() => handleChange("bezeichnung", s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Kürzel *</label>
          <input
            type="text"
            value={formData.kuerzel}
            onChange={(e) => handleChange("kuerzel", e.target.value)}
            placeholder="z.B. DTK"
          />
        </div>
        <div className="form-group priority-group">
          <label>Priorität</label>
          <input
            type="number"
            value={formData.priority}
            onChange={(e) => handleChange("priority", e.target.value)}
            min={1}
            max={1000}
          />
        </div>
      </div>
      {renderAdvancedFields()}
    </>
  );

  const renderEmpfaengerForm = () => (
    <>
      <div className="form-group">
        <label>Bezeichnung *</label>
        <input
          type="text"
          value={formData.bezeichnung}
          onChange={(e) => handleChange("bezeichnung", e.target.value)}
          placeholder="z.B. Max Mustermann"
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Kürzel</label>
          <input
            type="text"
            value={formData.kuerzel}
            onChange={(e) => handleChange("kuerzel", e.target.value)}
            placeholder="z.B. MM"
          />
        </div>
        <div className="form-group priority-group">
          <label>Priorität</label>
          <input
            type="number"
            value={formData.priority}
            onChange={(e) => handleChange("priority", e.target.value)}
            min={1}
            max={1000}
          />
        </div>
      </div>
      {renderAdvancedFields()}
    </>
  );

  const renderSchemaKeywordsForm = () => (
    <>
      <div className="form-group">
        <label>Schema *</label>
        <select
          value={formData.schema_code}
          onChange={(e) => handleChange("schema_code", e.target.value)}
        >
          <option value="">-- Schema wählen --</option>
          <option value="rechnung">Rechnung</option>
          <option value="versorger">Versorger</option>
          <option value="kontoauszug">Kontoauszug</option>
          <option value="bwa">BWA</option>
          <option value="sonstig">Sonstig</option>
          <option value="übergreifend">Übergreifend</option>
          <option value="blacklist">⛔ Blacklist</option>
        </select>
        {formData.schema_code === 'blacklist' && (
          <div style={{fontSize: '0.85em', color: '#666', marginTop: '4px', padding: '6px', background: '#fff3cd', borderRadius: '4px'}}>
            <strong>Blacklist-Format:</strong> Im Feld "Erweiterung" angeben:<br/>
            <code>ausschließen/bevorzugen</code> oder <code>schema1,schema2/bevorzugen</code>
          </div>
        )}
      </div>
      <div className="form-group">
        <label>Keyword *</label>
        <input
          type="text"
          value={formData.keyword}
          onChange={(e) => handleChange("keyword", e.target.value)}
          placeholder="z.B. Rechnung, Invoice, Beleg"
        />
        {prefillData.prefix_candidates?.length > 0 && (
          <div className="suggestions">
            <span className="suggestions-label">Erkannt:</span>
            {prefillData.prefix_candidates.slice(0, 3).map((s, i) => (
              <button 
                key={i} 
                type="button"
                className="suggestion-chip"
                onClick={() => handleChange("keyword", s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="form-group">
        <label>Match-Modus</label>
        <select
          value={formData.match_mode}
          onChange={(e) => handleChange("match_mode", e.target.value)}
        >
          <option value="exact">Exakt</option>
          <option value="contains">Enthält</option>
          <option value="regex">Regex</option>
          <option value="nummer">Nummer (rechts/unter Label)</option>
          <option value="zeitraum">Zeitraum (Leistungszeitraum)</option>
          <option value="darlehen">Darlehen (Darlehensnummer)</option>
        </select>
      </div>
      {renderAdvancedFields()}
    </>
  );

  const renderSpecialRulesForm = () => (
    <>
      <div className="form-group">
        <label>Geltungsbereich</label>
        <select
          value={formData.scope}
          onChange={(e) => handleChange("scope", e.target.value)}
        >
          <option value="global">Global</option>
          <option value="sender">Absender</option>
          <option value="schema">Schema</option>
          <option value="prefix">Prefix</option>
        </select>
      </div>
      {formData.scope !== "global" && (
        <div className="form-group">
          <label>Scope-Wert</label>
          <input
            type="text"
            value={formData.scope_value}
            onChange={(e) => handleChange("scope_value", e.target.value)}
            placeholder="z.B. Telekom"
          />
        </div>
      )}
      <div className="form-group">
        <label>Such-Token *</label>
        <input
          type="text"
          value={formData.anchor_token}
          onChange={(e) => handleChange("anchor_token", e.target.value)}
          placeholder="z.B. Rechnungsnummer"
        />
      </div>
      <div className="form-group">
        <label>Zielfeld</label>
        <select
          value={formData.target_field}
          onChange={(e) => handleChange("target_field", e.target.value)}
        >
          <option value="sender">Absender</option>
          <option value="invoice_no">Rechnungsnummer</option>
          <option value="date">Datum</option>
          <option value="prefix">Prefix</option>
        </select>
      </div>
      <div className="form-group">
        <label>Extract-Pattern (Regex)</label>
        <input
          type="text"
          value={formData.extract_pattern}
          onChange={(e) => handleChange("extract_pattern", e.target.value)}
          placeholder="z.B. (\d{10})"
        />
      </div>
      {renderAdvancedFields()}
    </>
  );

  const renderAdvancedFields = () => (
    <>
      <button 
        type="button" 
        className="advanced-toggle"
        onClick={() => setShowAdvanced(!showAdvanced)}
      >
        {showAdvanced ? <FaChevronUp /> : <FaChevronDown />}
        Erweiterte Optionen
      </button>
      {showAdvanced && (
        <div className="advanced-fields">
          <div className="form-group">
            <label>Priorität</label>
            <input
              type="number"
              value={formData.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
              min={1}
              max={1000}
            />
          </div>
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.aktiv}
                onChange={(e) => handleChange("aktiv", e.target.checked)}
              />
              Aktiv
            </label>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="add-entry-modal-overlay" onClick={onClose}>
      <div className="add-entry-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2>
            <FaPlus /> Eintrag hinzufügen
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </header>

        <div className="modal-body">
          {/* Tabellen-Auswahl */}
          <div className="form-group">
            <label>Tabelle</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
            >
              {TABLES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Error-Hinweis */}
          {errorTag && (
            <div className="error-context">
              <span className="error-tag">⚠️ Fehler: {errorTag.replace(/_/g, " ")}</span>
            </div>
          )}

          <hr />

          {/* Dynamisches Formular */}
          {renderForm()}

          {/* Fehler-Anzeige */}
          {error && (
            <div className="form-error">
              ⚠️ {error}
            </div>
          )}
        </div>

        <footer className="modal-footer">
          <button className="btn secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={handleSave} disabled={saving}>
            {saving ? "Speichert..." : "Speichern"}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AddEntryModal;
